"use client";

import { useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ChevronDown, Shield, Users, MapPin, Landmark, UserCheck, Network } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const hierarchyData = [{
  id: "mpn",
  label: "MPN",
  description: "Majelis Pimpinan Nasional"
}, {
  id: "mpw",
  label: "MPW",
  description: "Majelis Pimpinan Wilayah"
}, {
  id: "mpc",
  label: "MPC",
  description: "Majelis Pimpinan Cabang"
}, {
  id: "pac",
  label: "PAC",
  description: "Pimpinan Anak Cabang"
}, {
  id: "ranting",
  label: "RANTING",
  description: "Tingkat Kelurahan/Desa"
}, {
  id: "anak-ranting",
  label: "ANAK RANTING",
  description: "Tingkat RW/Basis"
}];
const detailData = [{
  id: "mpn",
  title: "Majelis Pimpinan Nasional",
  level: "Nasional (Pusat)",
  kedudukan: "Ibukota Negara",
  susunan: "Ketua Umum, Sekjen, Bendum, Bidang-Bidang",
  masaBakti: "5 Tahun",
  fungsi: "Penentu kebijakan nasional dan koordinasi tertinggi organisasi."
}, {
  id: "mpw",
  title: "Majelis Pimpinan Wilayah",
  level: "Provinsi",
  kedudukan: "Ibukota Provinsi",
  susunan: "Ketua, Sekretaris, Bendahara, Bidang-Bidang",
  masaBakti: "5 Tahun",
  fungsi: "Koordinasi tingkat provinsi dan pengawasan cabang."
}, {
  id: "mpc",
  title: "Majelis Pimpinan Cabang",
  level: "Kabupaten/Kota",
  kedudukan: "Ibukota Kabupaten/Kota",
  susunan: "Ketua, Sekretaris, Bendahara, Lembaga-Lembaga",
  masaBakti: "5 Tahun",
  fungsi: "Pelaksana kebijakan di tingkat daerah kabupaten/kota."
}, {
  id: "pac",
  title: "Pimpinan Anak Cabang",
  level: "Kecamatan",
  kedudukan: "Kecamatan",
  susunan: "Ketua, Sekretaris, Bendahara, Seksi-Seksi",
  masaBakti: "3 Tahun",
  fungsi: "Penggerak massa dan kaderisasi di tingkat kecamatan."
}, {
  id: "ranting",
  title: "Pimpinan Ranting",
  level: "Kelurahan/Desa",
  kedudukan: "Kelurahan/Desa",
  susunan: "Ketua, Sekretaris, Bendahara",
  masaBakti: "3 Tahun",
  fungsi: "Basis utama pergerakan di lingkungan masyarakat desa."
}, {
  id: "anak-ranting",
  title: "Pimpinan Anak Ranting",
  level: "RW / Basis",
  kedudukan: "Lingkungan RW",
  susunan: "Ketua, Sekretaris, Bendahara",
  masaBakti: "3 Tahun",
  fungsi: "Ujung tombak komunikasi langsung dengan warga."
}];
function VioletBeam({
  className = ""
}: {
  className?: string;
}) {
  return <div className={`flex items-center gap-2 ${className}`} aria-hidden="true">
      <div className="h-px flex-1 bg-border" />
      <div className="h-1.5 w-1.5 rounded-md bg-[var(--primary)]" />
    </div>;
}
export default function Nationalorganizationhierarchy() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [activeTab, setActiveTab] = useState("mpn");
  const currentDetail = detailData.find(d => d.id === activeTab) || detailData[0];
  return <section data-section-id="1888" id="hierarki" className="relative bg-white text-foreground py-28 md:py-36 overflow-hidden">
      <div ref={header.ref} className={`relative mx-auto max-w-[880px] px-5 md:px-8 text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-[calc(50%+60px)] font-default font-semibold text-[12rem] md:text-[22rem] leading-none tracking-tighter select-none" style={{
        WebkitTextStroke: "1px var(--border)",
        color: "transparent"
      }}>
          PP
        </span>

        <div className="relative z-10">
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.12em] text-muted-foreground mb-6">
            STRUKTUR ORGANISASI
          </p>
          <h2 className="font-default font-semibold text-[clamp(2.25rem,4.4vw,4rem)] leading-[1.02] tracking-tight">
            Hierarki Nasional,{" "}
            <span className="italic" style={{
            WebkitTextStroke: "1px var(--primary)",
            color: "transparent"
          }}>
              Terintegrasi
            </span>
            .
          </h2>
          <VioletBeam className="mt-8 mx-auto max-w-xs" />
        </div>
      </div>

      <div className="relative mx-auto max-w-[1200px] px-5 md:px-8 mt-20 md:mt-28">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
          {}
          <div className="w-full lg:w-1/2 flex flex-col items-center gap-4">
            {hierarchyData.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col items-center w-full">
                <Button variant="outline" onClick={() => setActiveTab(item.id)} className={`group relative w-full max-w-md h-auto p-6 md:p-8 bg-white border transition-all duration-300 text-center rounded-lg shadow-sm hover:shadow-md flex flex-col items-center justify-center whitespace-normal ${activeTab === item.id ? "border-[var(--primary)] ring-1 ring-[var(--primary)]" : "border-border hover:border-[var(--primary)]"}`}>
                  <div className={`absolute -top-px -left-px h-2 w-2 border-t border-l border-[var(--primary)] transition-opacity ${activeTab === item.id ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
                  <div className={`absolute -bottom-px -right-px h-2 w-2 border-b border-r border-[var(--primary)] transition-opacity ${activeTab === item.id ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
                  
                  <h3 className="font-mono text-2xl md:text-3xl font-bold text-[var(--primary)] mb-1">
                    {item.label}
                  </h3>
                  <p className="font-default text-sm md:text-base text-muted-foreground uppercase tracking-wider">
                    {item.description}
                  </p>
                </Button>

                {i < hierarchyData.length - 1 && <div className="flex flex-col items-center py-4">
                    <div className="w-px h-12 bg-gradient-to-b from-[var(--primary)] to-transparent" />
                    <ChevronDown className="size-6 text-[var(--primary)] -mt-1 animate-bounce" />
                  </div>}
              </div>)}
          </div>

          {}
          <div className="w-full lg:w-1/2 sticky top-24">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-8">
              <TabsList className="grid grid-cols-3 md:grid-cols-6 w-full h-auto p-1 bg-muted rounded-lg">
                {hierarchyData.map(item => <TabsTrigger key={item.id} value={item.id} className="text-[10px] md:text-xs py-2 px-1 data-[state=active]:bg-white data-[state=active]:text-[var(--primary)] data-[state=active]:shadow-sm rounded-md">
                    {item.label}
                  </TabsTrigger>)}
              </TabsList>
            </Tabs>

            <Card className="border-border shadow-md rounded-lg overflow-hidden">
              <CardHeader className="bg-muted/30 border-b border-border pb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[var(--primary)]/10 rounded-md">
                    <Shield className="size-5 text-[var(--primary)]" />
                  </div>
                  <span className="font-mono text-xs font-bold text-[var(--primary)] tracking-widest uppercase">
                    Detail Tingkatan
                  </span>
                </div>
                <CardTitle className="text-2xl md:text-3xl font-bold tracking-tight">
                  {currentDetail.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Landmark className="size-4" />
                      <span className="text-xs font-semibold uppercase tracking-wider">Level</span>
                    </div>
                    <p className="font-medium">{currentDetail.level}</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <MapPin className="size-4" />
                      <span className="text-xs font-semibold uppercase tracking-wider">Kedudukan</span>
                    </div>
                    <p className="font-medium">{currentDetail.kedudukan}</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Network className="size-4" />
                      <span className="text-xs font-semibold uppercase tracking-wider">Susunan</span>
                    </div>
                    <p className="font-medium">{currentDetail.susunan}</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <UserCheck className="size-4" />
                      <span className="text-xs font-semibold uppercase tracking-wider">Masa Bakti</span>
                    </div>
                    <p className="font-medium">{currentDetail.masaBakti}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-border">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <Users className="size-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Fungsi Utama</span>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    {currentDetail.fungsi}
                  </p>
                </div>
              </CardContent>
              <CardFooter className="bg-muted/10 border-t border-border p-6">
                <Button className="w-full bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white rounded-md py-6 text-base font-semibold shadow-md">
                  Lihat Struktur Pengurus {currentDetail.id.toUpperCase()}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}